/**
 * Befund S2 (Konto-Prüfung 03.11.2026, Inhaber 04.11.2026): Dieselbe Person darf ein eigenes Creator-Konto (Konto der Art
 * „creator“, 0178) und ein Creator-Profil bei einer Agentur haben. Beide Bereiche bleiben getrennt (Daten, Rechnungen,
 * Instagram, 0540); nach der Anmeldung und im Bereichswechsel wählt die Person klar zwischen „Mein Konto“ und „Bei <Agentur>“.
 *
 * Ziele sind gewöhnliche Adressen mit ?bereich=<Konto>: Der Bereichsrahmen übernimmt die Wahl (app/layouts.tsx,
 * BereichAusAdresse) und räumt die Adresse auf. So funktionieren dieselben Ziele in der Bereichswahl, im Kopf, in der
 * Navigation und auf „Mehr“.
 */

export type Arbeitsbereich =
  | { art: "eigen"; orgId: string; name: string; to: string }
  | { art: "agentur"; orgId: string; name: string; to: string };

type Konto = {
  memberships: readonly { org_id: string }[];
  talentLinks: readonly { org_id: string }[];
  orgs: Readonly<Record<string, { name: string; kind?: "agency" | "creator" } | undefined>>;
};

/** Eigenes Creator-Konto und Creator-Profile bei lesbaren Agenturen (archivierte Profile zählen nicht: dort gibt es nur Belege) */
export function arbeitsbereiche(a: Konto): Arbeitsbereich[] {
  const eigen = a.memberships
    .filter((m) => a.orgs[m.org_id]?.kind === "creator")
    .map((m) => ({ art: "eigen" as const, orgId: m.org_id, name: a.orgs[m.org_id]!.name, to: `/agentur?bereich=${m.org_id}` }));
  const agenturen = [...new Set(a.talentLinks.filter((l) => a.orgs[l.org_id] && a.orgs[l.org_id]!.kind !== "creator").map((l) => l.org_id))]
    .map((org) => ({ art: "agentur" as const, orgId: org, name: a.orgs[org]!.name, to: `/creator?bereich=${org}` }));
  return [...eigen, ...agenturen];
}

/** Eigenes Creator-Konto und mindestens ein Profil bei einer Agentur: nach der Anmeldung die Bereichswahl zeigen */
export function brauchtBereichswahl(a: Konto): boolean {
  const b = arbeitsbereiche(a);
  return b.some((x) => x.art === "eigen") && b.some((x) => x.art === "agentur");
}

/** Beschriftung im Wechsel: „Mein Konto“ bzw. „Bei <Agentur>“ */
export function bereichName(b: Pick<Arbeitsbereich, "art" | "name">, t: (de: string, en: string) => string): string {
  return b.art === "eigen" ? t("Mein Konto", "My account") : t(`Bei ${b.name}`, `At ${b.name}`);
}

/**
 * Praxistest 04.11.2026: Der Satz unter der Bereichswahl nennt die echte Beschriftung der Auswahl oben im Kopf („Mein Konto“ bzw.
 * „Bei <Agentur>“), keinen Knopf „Bereich wechseln“, den es so nicht gibt.
 */
export function wechselSatz(bereiche: readonly Pick<Arbeitsbereich, "art" | "name">[], t: (de: string, en: string) => string): string {
  const namen = bereiche.map((b) => bereichName(b, t));
  const zitat = (n: string) => t(`„${n}“`, `“${n}”`);
  const liste = agenturNamenText(namen.map(zitat), t("bzw.", "or"));
  return t(`Wechseln kannst du jederzeit oben im Kopf über die Auswahl ${liste}.`, `You can switch at any time at the top using the selector ${liste}.`);
}

/** Die übrigen Bereiche aus Sicht des gezeigten Bereichs (für Navigation und „Mehr“) */
export function andereBereiche(a: Konto, aktuell: { art: Arbeitsbereich["art"]; orgId: string }): Arbeitsbereich[] {
  return arbeitsbereiche(a).filter((b) => !(b.art === aktuell.art && b.orgId === aktuell.orgId));
}

/** Wert aus ?bereich=…, nur wenn die Person diesen Bereich wirklich hat; sonst null (die Adresse wird trotzdem aufgeräumt) */
export function bereichAusAdresse(search: string, art: Arbeitsbereich["art"] | "team", a: Konto): string | null {
  const ziel = new URLSearchParams(search).get("bereich");
  if (!ziel) return null;
  if (art === "agentur") return a.talentLinks.some((l) => l.org_id === ziel) ? ziel : null;
  return a.memberships.some((m) => m.org_id === ziel) ? ziel : null;
}

/** Hinweis beim Anlegen eines Creator-Kontos: Namen der Agenturen, bei denen die Person schon ein Creator-Profil hat */
export function agenturNamenText(namen: readonly string[], und: string): string {
  if (namen.length <= 1) return namen[0] ?? "";
  return `${namen.slice(0, -1).join(", ")} ${und} ${namen[namen.length - 1]}`;
}

/** Zuletzt genutzter Bereich: Team- bzw. Creator-Bereich (tk-area) und das dort zuletzt gewählte Konto */
export function zuletztGenutzt(b: Arbeitsbereich, zuletzt: string | null, org: { team: string | null; creator: string | null }): boolean {
  if (b.art === "eigen") return zuletzt !== "creator" && (org.team === null || org.team === b.orgId);
  return zuletzt === "creator" && (org.creator === null || org.creator === b.orgId);
}
